import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getDossierDetail, triggerScoring, getAdminDataroom, getAdminDataroomDownloadUrl, updateDataroomDocStatus, updateDossierAction, getStartupRadar, getBodaccForStartup, getStartupEvents, publishScore, getQitDefinition } from '../../api';
import { ScoreGauge, RiskBadge } from '../../components/ScoreGauge';
import RadarChart from '../../components/RadarChart';
import { useToast } from '../../components/Toast';

const ACTION_LABELS = {
  nouveau: { label: 'Nouveau', color: '#2196F3', bg: '#E3F2FD' },
  en_cours: { label: 'En cours', color: '#FF9800', bg: '#FFF3E0' },
  valide: { label: 'Validé', color: '#4CAF50', bg: '#E8F5E9' },
  en_attente: { label: 'En attente', color: '#9C27B0', bg: '#F3E5F5' },
  rejete: { label: 'Rejeté', color: '#F44336', bg: '#FFEBEE' },
  publie: { label: 'Publié', color: '#00897B', bg: '#E0F2F1' },
};
const DOC_STATUS_MAP = {
  en_attente: { label: 'En attente', color: '#F39C12' },
  valide: { label: 'Validé', color: '#27AE60' },
  rejete: { label: 'Rejeté', color: '#E74C3C' },
  a_completer: { label: 'À compléter', color: '#E67E22' }
};
const BODACC_COLORS = {
  'Création': { color: '#27AE60', bg: '#E8F5E9', icon: '🏢' },
  'Ventes et cessions': { color: '#E67E22', bg: '#FFF3E0', icon: '💰' },
  'Procédures collectives': { color: '#E74C3C', bg: '#FFEBEE', icon: '⚠️' },
  'Modification': { color: '#2196F3', bg: '#E3F2FD', icon: '✏️' },
  'Radiation': { color: '#9E9E9E', bg: '#F5F5F5', icon: '❌' },
};

// Parse BODACC description: if it's a JSON string, extract readable info
function parseBodaccDescription(desc) {
  if (!desc) return null;
  const trimmed = desc.trim();
  // Check if it looks like JSON (starts with { or ")
  if (trimmed.startsWith('"') || trimmed.startsWith('{')) {
    try {
      let parsed = trimmed.startsWith('"') ? JSON.parse(trimmed) : trimmed;
      if (typeof parsed === 'string' && (parsed.startsWith('{') || parsed.startsWith('['))) {
        parsed = JSON.parse(parsed);
      }
      if (typeof parsed === 'object' && parsed !== null) {
        const p = parsed.personne || parsed;
        const parts = [];
        if (p.denomination) parts.push(p.denomination);
        if (p.formeJuridique) parts.push(p.formeJuridique);
        if (p.capital) {
          const montant = parseFloat(p.capital.montantCapital || 0);
          const devise = p.capital.devise || 'EUR';
          if (montant) parts.push(`Capital : ${montant.toLocaleString('fr-FR')} ${devise}`);
        }
        if (p.administration) parts.push(p.administration);
        if (p.activite) parts.push(p.activite);
        const addr = p.adresseSiegeSocial;
        if (addr) {
          const addrParts = [addr.numeroVoie, addr.typeVoie, addr.nomVoie, addr.codePostal, addr.ville].filter(Boolean);
          if (addrParts.length) parts.push(`Siège : ${addrParts.join(' ')}`);
        }
        if (p.numeroImmatriculation) {
          const imm = p.numeroImmatriculation;
          parts.push(`${imm.codeRCS || 'RCS'} ${imm.nomGreffeImmat || ''} ${imm.numeroIdentification || ''}`.trim());
        }
        return parts.length ? parts : null;
      }
    } catch (e) { /* not JSON, return as-is */ }
  }
  return null;
}

function CollapseHeader({ title, badge, open, onToggle, children }) {
  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
        onClick={onToggle}>
        <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          {title}
          {badge}
        </h3>
        <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--text-light)', padding: '0 4px' }}
          onClick={e => { e.stopPropagation(); onToggle(); }}>
          {open ? '▲' : '▼'}
        </button>
      </div>
      {open && <div style={{ marginTop: 16 }}>{children}</div>}
    </div>
  );
}

export default function DossierDetail() {
  const { id } = useParams();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [scoring, setScoring] = useState(false);
  const [dataroom, setDataroom] = useState({ documents: [], events: [] });
  const [dataroomOpen, setDataroomOpen] = useState(true);
  const [auditOpen, setAuditOpen] = useState(false);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [infoOpen, setInfoOpen] = useState(true);
  const [qitOpen, setQitOpen] = useState(false);
  const [radarOpen, setRadarOpen] = useState(true);
  const [radarData, setRadarData] = useState(null);
  const [bodaccOpen, setBodaccOpen] = useState(true);
  const [bodaccData, setBodaccData] = useState([]);
  const [showMap, setShowMap] = useState(false);
  const [docViewer, setDocViewer] = useState(null); // { doc, blobUrl }
  const [docViewerReason, setDocViewerReason] = useState('');
  const [docViewerAdminComment, setDocViewerAdminComment] = useState('');
  const [openClusters, setOpenClusters] = useState({}); // { 'CL-01': true, ... }
  const [qitDefinition, setQitDefinition] = useState(null);

  // Charge la définition QIT une seule fois (commun à toutes les startups)
  useEffect(() => {
    getQitDefinition().then(setQitDefinition).catch(() => setQitDefinition(null));
  }, []);

  // Lookup question_id -> { text, section } construit a partir de la definition
  const questionLookup = React.useMemo(() => {
    if (!qitDefinition) return {};
    const map = {};
    Object.values(qitDefinition).forEach(cluster => {
      (cluster.questions || []).forEach(q => {
        map[q.id] = { text: q.text, section: q.section || null };
      });
    });
    return map;
  }, [qitDefinition]);

  const load = () => {
    getDossierDetail(id).then(d => {
      setData(d);
      if (d.startup && d.startup.note_sectorielle) {
        getStartupRadar(d.startup.startup_id || d.startup.id || id).then(setRadarData).catch(() => setRadarData(null));
      }
      getBodaccForStartup(id).then(r => setBodaccData(r.records || r || [])).catch(() => setBodaccData([]));
    }).catch(console.error);
    getAdminDataroom(id).then(setDataroom).catch(() => setDataroom({ documents: [], events: [] }));
  };
  useEffect(() => { load(); }, [id]);

  const handleActionChange = async (action) => {
    try {
      await updateDossierAction(id, action);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const handleDocStatus = async (docId, status, reason, adminComment) => {
    try {
      await updateDataroomDocStatus(docId, status, reason, adminComment);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const handleScoring = async () => {
    setScoring(true);
    try {
      const result = await triggerScoring(id);
      toast.success(`Score calculé ! Note ISI: ${result.note_sectorielle} (${result.risk_level})`);
      load();
    } catch (err) {
      toast.error(err.message);
    }
    setScoring(false);
  };

  const handlePublishScore = async (published) => {
    try {
      await publishScore(id, published);
      toast.success(published
        ? 'Score ISI publié — la startup peut désormais le voir'
        : 'Publication du score ISI retirée');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDownloadDoc = async (doc) => {
    try {
      const token = localStorage.getItem('isi_token');
      const res = await fetch(getAdminDataroomDownloadUrl(id, doc.id), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erreur de téléchargement');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.message); }
  };

  const [docLoading, setDocLoading] = useState(false);

  const openDocViewer = async (doc) => {
    try {
      setDocLoading(true);
      const token = localStorage.getItem('isi_token');
      const res = await fetch(getAdminDataroomDownloadUrl(id, doc.id), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.detail || errBody.message || `Erreur ${res.status} lors du téléchargement`);
      }
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      setDocViewer({ doc, blobUrl, mimeType: doc.mime_type || blob.type });
      setDocViewerReason('');
      setDocViewerAdminComment(doc.admin_comment || '');
    } catch (err) { toast.error(err.message); }
    finally { setDocLoading(false); }
  };

  const closeDocViewer = () => {
    if (docViewer?.blobUrl) window.URL.revokeObjectURL(docViewer.blobUrl);
    setDocViewer(null);
    setDocViewerReason('');
    setDocViewerAdminComment('');
  };

  const handleDocViewerAction = async (status, reason) => {
    if (!docViewer) return;
    try {
      await handleDocStatus(docViewer.doc.id, status, reason, docViewerAdminComment.trim() || undefined);
      closeDocViewer();
    } catch (err) { toast.error(err.message); }
  };

  if (!data) return <div className="loading">Chargement du dossier...</div>;
  if (!data.startup) return (
    <div className="container" style={{ padding: '60px 0', textAlign: 'center' }}>
      <h2 style={{ color: '#E74C3C' }}>Startup introuvable</h2>
      <p style={{ color: 'var(--text-light)', marginTop: 8 }}>Le dossier #{id} n'existe pas ou a été supprimé.</p>
      <Link to="/analyst/dossiers" className="btn btn-primary" style={{ marginTop: 16 }}>Retour aux dossiers</Link>
    </div>
  );

  const { startup: s, qit_responses, audit_alerts } = data;
  const docs = dataroom.documents || [];
  const docEvents = dataroom.events || [];
  const currentAction = s.dossier_action || null;

  // Group documents by category
  const docsByCategory = {};
  docs.forEach(d => {
    if (!docsByCategory[d.category]) docsByCategory[d.category] = [];
    docsByCategory[d.category].push(d);
  });

  const catLabels = { GENERAL: 'I. Informations Générales', COMPTABILITE: 'II. Comptabilité', CONTRATS: 'III. Contrats et Avenants' };

  // Map URL for geolocation
  const hasGeo = s.latitude && s.longitude;
  const mapUrl = hasGeo ? `https://www.openstreetmap.org/export/embed.html?bbox=${s.longitude - 0.005},${s.latitude - 0.003},${s.longitude + 0.005},${s.latitude + 0.003}&layer=mapnik&marker=${s.latitude},${s.longitude}` : null;

  return (
    <div style={{ padding: '32px 0' }}>
      <div className="container">
        <Link to="/analyst/dossiers" style={{ fontSize: 14, color: 'var(--text-light)' }}>&larr; Retour aux dossiers</Link>

        {/* Header with title */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: 16, marginBottom: 24 }}>
          <div>
            <h1 style={{ marginBottom: 4, fontSize: 22 }}>
              Traitement de la candidature de <span style={{ color: 'var(--primary)' }}>{s.startup_name || s.name}</span>
            </h1>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              {s.categorie && <span className="badge badge-secteur">{s.categorie}</span>}
              {s.risk_level && <RiskBadge level={s.risk_level} />}
              {currentAction && ACTION_LABELS[currentAction] && (
                <span className="badge" style={{ background: ACTION_LABELS[currentAction].bg, color: ACTION_LABELS[currentAction].color, fontWeight: 600 }}>
                  {ACTION_LABELS[currentAction].label}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={handleScoring} className="btn btn-accent" disabled={scoring}>
              {scoring ? 'Calcul...' : 'Déclencher le scoring'}
            </button>
            {s.note_sectorielle && (
              s.score_published ? (
                <button onClick={() => handlePublishScore(false)} className="btn btn-outline"
                  style={{ borderColor: '#E67E22', color: '#E67E22' }}
                  title="Retirer la visibilité du score sur le dashboard de la startup">
                  Dépublier le score
                </button>
              ) : (
                <button onClick={() => handlePublishScore(true)} className="btn btn-primary"
                  title="Rendre le score ISI visible sur le dashboard de la startup et l'avertir par un événement">
                  Publier le score à la startup
                </button>
              )
            )}
          </div>
        </div>

        {/* Dossier action buttons */}
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 12 }}>Actions sur le dossier</h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {Object.entries(ACTION_LABELS).map(([key, info]) => (
              <button key={key} onClick={() => handleActionChange(key)}
                style={{
                  fontSize: 12, padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
                  fontFamily: 'inherit', fontWeight: currentAction === key ? 700 : 500,
                  border: `2px solid ${currentAction === key ? info.color : 'var(--border)'}`,
                  background: currentAction === key ? info.bg : 'white',
                  color: currentAction === key ? info.color : 'var(--text)',
                  transition: 'all 0.15s ease',
                }}>
                {info.label}
              </button>
            ))}
            {currentAction && (
              <button onClick={() => handleActionChange(null)}
                style={{ fontSize: 11, padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                  fontFamily: 'inherit', border: '1px solid var(--border)', background: '#FAFAFA',
                  color: 'var(--text-light)' }}>
                Aucune action
              </button>
            )}
          </div>
        </div>

        {/* Informations (merged with Score ISI) — collapsible */}
        <CollapseHeader title="Informations" open={infoOpen} onToggle={() => setInfoOpen(!infoOpen)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {/* Left: Score ISI */}
            <div>
              <h4 style={{ fontSize: 14, color: 'var(--primary)', marginBottom: 12, borderBottom: '1px solid var(--border)', paddingBottom: 6 }}>Score ISI</h4>
              {s.note_sectorielle ? (
                <div style={{ textAlign: 'center' }}>
                  <ScoreGauge score={s.note_sectorielle} size="lg" />
                  <div style={{ marginTop: 12, fontSize: 14 }}>
                    <div>Note Pivot : <strong>{parseFloat(s.note_pivot).toFixed(2)}</strong></div>
                    <div>Cohérence : <strong>{s.taux_coherence ? `${parseFloat(s.taux_coherence).toFixed(0)}%` : '-'}</strong></div>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-light)' }}>Score non calculé</div>
              )}
            </div>

            {/* Right: Info table */}
            <div>
              <h4 style={{ fontSize: 14, color: 'var(--primary)', marginBottom: 12, borderBottom: '1px solid var(--border)', paddingBottom: 6 }}>Fiche startup</h4>
              <table style={{ width: '100%', fontSize: 13 }}>
                <tbody>
                  {[
                    ['Secteur', s.categorie],
                    ['Sous-domaine', s.sous_domaine],
                    ['Maturité', s.maturite],
                    ['CA', s.ca],
                    ['Clientèle', s.adressage_client],
                    ['Round', s.round_levee],
                    ['QIT', `${Math.max(0, Math.min(100, s.qit_progress || 0))}%`],
                    ['SIRET', s.siret],
                  ].map(([k, v]) => (
                    <tr key={k} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '6px 8px', color: 'var(--text-light)', width: '40%' }}>{k}</td>
                      <td style={{ padding: '6px 8px', fontWeight: 500 }}>{v || '-'}</td>
                    </tr>
                  ))}
                  {/* Site web with link */}
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '6px 8px', color: 'var(--text-light)', width: '40%' }}>Site web</td>
                    <td style={{ padding: '6px 8px', fontWeight: 500 }}>
                      {s.site_web ? (
                        <a href={s.site_web} target="_blank" rel="noopener noreferrer"
                          style={{ color: 'var(--primary)', textDecoration: 'none' }}>
                          {s.site_web.replace(/^https?:\/\//, '')} &#x2197;
                        </a>
                      ) : '-'}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Geolocation button */}
              {hasGeo && (
                <button onClick={() => setShowMap(!showMap)}
                  style={{ marginTop: 12, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)',
                    background: showMap ? '#E3F2FD' : 'white', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12,
                    color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {showMap ? '🗺 Masquer la carte' : '🗺 Voir sur la carte'}
                </button>
              )}
            </div>
          </div>

          {/* Map display */}
          {showMap && mapUrl && (
            <div style={{ marginTop: 16, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
              <iframe title="Localisation startup" src={mapUrl}
                style={{ width: '100%', height: 300, border: 'none' }} />
              <div style={{ padding: '8px 12px', background: '#F5F9FF', fontSize: 12, color: 'var(--text-light)' }}>
                {s.adresse || `${s.latitude}, ${s.longitude}`}
              </div>
            </div>
          )}
        </CollapseHeader>

        {/* Radar Chart — collapsible */}
        <CollapseHeader title="Radar de Résilience" open={radarOpen} onToggle={() => setRadarOpen(!radarOpen)}>
          {radarData ? (
            <RadarChart
              labels={radarData.labels}
              startupData={radarData.startup}
              sectorAvg={radarData.sector_avg}
              startupName={s.startup_name || s.name}
              height={400}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-light)' }}>
              {s.note_sectorielle ? 'Chargement du radar...' : 'Données radar non disponibles (score non calculé)'}
            </div>
          )}
        </CollapseHeader>

        {/* QIT Responses — collapsible, grouped by cluster */}
        <CollapseHeader
          title="Réponses QIT"
          badge={<span style={{ fontSize: 12, color: 'var(--text-light)', fontWeight: 400, marginLeft: 4 }}>({qit_responses.length} réponses)</span>}
          open={qitOpen}
          onToggle={() => setQitOpen(!qitOpen)}>
          {qit_responses.length === 0 ? (
            <p style={{ color: 'var(--text-light)' }}>Aucune réponse soumise</p>
          ) : (() => {
            const CLUSTER_META = {
              'CL-01': { name: 'Business Plan', scoreKey: 'cl01_bp' },
              'CL-02': { name: 'Ratio Efficacité Commerciale', scoreKey: 'cl02_ratio' },
              'CL-03': { name: 'Solidité Financière', scoreKey: 'cl03_solidite_fi' },
              'CL-04': { name: 'Maturité Financière', scoreKey: 'cl04_maturite_fi' },
              'CL-05': { name: 'Marché', scoreKey: 'cl05_marche' },
              'CL-06': { name: 'Innovation', scoreKey: 'cl06_innovation' },
              'CL-07': { name: 'Équipe Dirigeante', scoreKey: 'cl07_equipe' },
              'CL-08': { name: 'Organisation Commerciale', scoreKey: 'cl08_org_comm' },
              'CL-09': { name: 'Notoriété', scoreKey: 'cl09_notoriete' },
              'CL-10': { name: 'RSE', scoreKey: 'cl10_rse' },
              'CL-11': { name: 'RGPD / Sécurité', scoreKey: 'cl11_rgpd' },
              'CL-12': { name: 'Conformité', scoreKey: 'cl12_conformite' },
            };
            // Group responses by cluster
            const byCluster = {};
            qit_responses.forEach(r => {
              if (!byCluster[r.cluster_id]) byCluster[r.cluster_id] = [];
              byCluster[r.cluster_id].push(r);
            });
            const clusterIds = Object.keys(CLUSTER_META).filter(cid => byCluster[cid]);

            return (
              <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                {clusterIds.map(cid => {
                  const meta = CLUSTER_META[cid];
                  const score = s[meta.scoreKey];
                  const scoreNum = score != null ? parseFloat(score) : null;
                  const scoreColor = scoreNum != null ? (scoreNum >= 7 ? '#27AE60' : scoreNum >= 4 ? '#F39C12' : '#E74C3C') : '#999';
                  const responses = byCluster[cid] || [];

                  const isOpen = !!openClusters[cid];
                  const toggleCluster = () => setOpenClusters(prev => ({ ...prev, [cid]: !prev[cid] }));

                  return (
                    <div key={cid} style={{ marginBottom: 8 }}>
                      {/* Cluster header with score — clickable */}
                      <div onClick={toggleCluster}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '10px 14px', background: '#F5F9FF', borderRadius: 8,
                        borderLeft: `4px solid ${scoreColor}`, cursor: 'pointer',
                        transition: 'background 0.15s ease' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#EBF3FE'}
                        onMouseLeave={e => e.currentTarget.style.background = '#F5F9FF'}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 12, color: 'var(--text-light)', width: 14, textAlign: 'center' }}>
                            {isOpen ? '▼' : '▶'}
                          </span>
                          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', background: '#E3F2FD',
                            padding: '2px 8px', borderRadius: 4 }}>{cid}</span>
                          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{meta.name}</span>
                          <span style={{ fontSize: 11, color: 'var(--text-light)' }}>({responses.length} rép.)</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 11, color: 'var(--text-light)' }}>Score ISI :</span>
                          {scoreNum != null ? (
                            <span style={{ fontSize: 16, fontWeight: 700, color: scoreColor }}>
                              {scoreNum.toFixed(2)}<span style={{ fontSize: 11, fontWeight: 400 }}>/10</span>
                            </span>
                          ) : (
                            <span style={{ fontSize: 13, color: '#999', fontStyle: 'italic' }}>Non calculé</span>
                          )}
                        </div>
                      </div>

                      {/* Questions table — collapsed by default */}
                      {isOpen && (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 4 }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid var(--border)' }}>
                            <th style={{ padding: '6px 8px', textAlign: 'left', width: 60 }}>ID</th>
                            <th style={{ padding: '6px 8px', textAlign: 'left' }}>Question</th>
                            <th style={{ padding: '6px 8px', textAlign: 'left', width: '32%' }}>Valeur déclarée</th>
                            <th style={{ padding: '6px 8px', textAlign: 'center', width: 80 }}>Statut</th>
                          </tr>
                        </thead>
                        <tbody>
                          {responses.map(r => {
                            const qInfo = questionLookup[r.question_id];
                            return (
                              <tr key={r.id} style={{ borderBottom: '1px solid var(--border)', verticalAlign: 'top' }}>
                                <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: 11, color: 'var(--primary)', whiteSpace: 'nowrap' }}>
                                  {r.question_id}
                                  {qInfo?.section && (
                                    <div style={{ fontSize: 9, color: 'var(--text-light)', fontFamily: 'inherit', marginTop: 2 }}>{qInfo.section}</div>
                                  )}
                                </td>
                                <td style={{ padding: '8px', fontSize: 12, lineHeight: 1.4, color: 'var(--text)' }}>
                                  {qInfo?.text ? qInfo.text : (
                                    <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                      Question non trouvée dans la définition courante
                                    </span>
                                  )}
                                </td>
                                <td style={{ padding: '8px', fontWeight: 500, wordBreak: 'break-word' }}>{r.declared_value}</td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                  <span className={`badge badge-${r.coherence_status === 'ok' ? 'vert' : 'orange'}`} style={{ fontSize: 10 }}>
                                    {r.coherence_status}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      )}
                    </div>
                  );
                })}

                {/* Clusters sans réponse — ligne récapitulative en bas */}
                {(() => {
                  const missing = Object.keys(CLUSTER_META).filter(cid => !byCluster[cid]);
                  if (missing.length === 0) return null;
                  return (
                    <div style={{
                      marginTop: 16, padding: '12px 14px',
                      background: '#FFF8E1', borderRadius: 8,
                      borderLeft: '4px solid #F39C12',
                      fontSize: 12, color: 'var(--text)',
                    }}>
                      <strong style={{ color: '#E65100', marginRight: 6 }}>
                        Cluster(s) sans réponse ({missing.length}/{Object.keys(CLUSTER_META).length}) :
                      </strong>
                      <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6, marginLeft: 4 }}>
                        {missing.map(cid => (
                          <span key={cid} className="badge"
                            style={{
                              background: 'white', color: 'var(--text-light)',
                              border: '1px solid var(--border)', fontSize: 11,
                              padding: '3px 9px',
                            }}
                            title={CLUSTER_META[cid].name}>
                            <strong style={{ color: 'var(--primary)', marginRight: 4 }}>{cid}</strong>
                            {CLUSTER_META[cid].name}
                          </span>
                        ))}
                      </span>
                    </div>
                  );
                })()}
              </div>
            );
          })()}
        </CollapseHeader>

        {/* Data Room */}
        <div className="card" style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', marginBottom: dataroomOpen ? 16 : 0 }}
            onClick={() => setDataroomOpen(!dataroomOpen)}>
            <h3 style={{ margin: 0 }}>
              Data Room ({docs.length} documents)
              {docs.filter(d => d.status === 'en_attente').length > 0 && (
                <span className="badge" style={{ marginLeft: 8, background: '#FFF3E0', color: '#E65100', fontSize: 10 }}>
                  {docs.filter(d => d.status === 'en_attente').length} en attente
                </span>
              )}
            </h3>
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--text-light)' }}
              onClick={e => { e.stopPropagation(); setDataroomOpen(!dataroomOpen); }}>
              {dataroomOpen ? '▲' : '▼'}
            </button>
          </div>

          {dataroomOpen && (
            <>
              {docs.length === 0 ? (
                <p style={{ color: 'var(--text-light)' }}>Aucun document déposé</p>
              ) : (
                Object.entries(docsByCategory).map(([cat, catDocs]) => (
                  <div key={cat} style={{ marginBottom: 16 }}>
                    <h4 style={{ fontSize: 14, color: 'var(--primary)', marginBottom: 10 }}>{catLabels[cat] || cat}</h4>
                    <div style={{ display: 'grid', gap: 8 }}>
                      {catDocs.map(d => (
                        <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 8, background: '#FAFAFA' }}>
                          <div style={{ flex: 1 }}>
                            <strong style={{ fontSize: 13 }}>{d.document_type}</strong>
                            <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 2 }}>
                              {d.file_name} — {(d.file_size / 1024).toFixed(0)} Ko
                              {d.sha256_hash && (
                                <span style={{ marginLeft: 8, fontFamily: 'monospace', fontSize: 10, color: '#999' }}
                                  title={`SHA-256: ${d.sha256_hash}`}>
                                  SHA: {d.sha256_hash.substring(0, 16)}...
                                </span>
                              )}
                            </div>
                            {d.uploaded_by_email && (
                              <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>
                                Déposé par {d.uploaded_by_email} le {new Date(d.uploaded_at).toLocaleString('fr-FR')}
                              </div>
                            )}
                            {d.rejection_reason && (d.status === 'rejete' || d.status === 'a_completer') && (
                              <div style={{ fontSize: 11, color: d.status === 'rejete' ? '#E74C3C' : '#E67E22', marginTop: 4 }}>
                                Motif : {d.rejection_reason}
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                            <span className="badge" style={{
                              background: `${DOC_STATUS_MAP[d.status]?.color}20`,
                              color: DOC_STATUS_MAP[d.status]?.color, fontSize: 10
                            }}>
                              {DOC_STATUS_MAP[d.status]?.label || d.status}
                            </span>
                            {d.archive_path ? (
                              <button onClick={() => openDocViewer(d)}
                                style={{ padding: '4px 12px', border: '1px solid var(--primary)', borderRadius: 6,
                                  background: '#F5F9FF', cursor: 'pointer', fontSize: 11, fontFamily: 'inherit',
                                  color: 'var(--primary)', fontWeight: 600 }}
                                title="Consulter le document">
                                Voir
                              </button>
                            ) : (
                              <span style={{ fontSize: 10, color: '#999', fontStyle: 'italic' }}>Pas de fichier</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}

              {/* Audit trail */}
              {docEvents.length > 0 && (
                <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: auditOpen ? 8 : 0 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-light)' }}>
                      Journal d'audit ({docEvents.length} événements)
                    </span>
                    <button onClick={() => setAuditOpen(!auditOpen)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--primary)' }}>
                      {auditOpen ? 'Masquer' : 'Afficher'}
                    </button>
                  </div>
                  {auditOpen && (
                    <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                      {docEvents.map(ev => (
                        <div key={ev.id} style={{ padding: '6px 0', borderBottom: '1px solid #f0f0f0', fontSize: 11, color: '#666' }}>
                          <span style={{ fontWeight: 500, color: '#333' }}>{new Date(ev.created_at).toLocaleString('fr-FR')}</span>
                          {' — '}
                          <span style={{ fontFamily: 'monospace', fontSize: 10, color: 'var(--primary)' }}>{ev.event_type}</span>
                          {' — '}
                          {ev.event_description}
                          {ev.user_email && <span style={{ color: '#999' }}> ({ev.user_email})</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* BODACC — Annonces légales */}
        <CollapseHeader
          title="Dernières annonces légales (BODACC)"
          badge={<span style={{ fontSize: 12, color: 'var(--text-light)', fontWeight: 400, marginLeft: 4 }}>({bodaccData.length})</span>}
          open={bodaccOpen}
          onToggle={() => setBodaccOpen(!bodaccOpen)}>
          {bodaccData.length === 0 ? (
            <p style={{ color: 'var(--text-light)' }}>Aucune annonce BODACC trouvée pour cette startup.</p>
          ) : (
            <>
              {bodaccData[0]?.fetched_at && (
                <div style={{ fontSize: 11, color: 'var(--text-light)', marginBottom: 12 }}>
                  Dernière mise à jour : {new Date(bodaccData[0].fetched_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
              )}
              <div style={{ display: 'grid', gap: 10 }}>
                {bodaccData.map((b, idx) => {
                  const info = BODACC_COLORS[b.famille_avis] || { color: '#666', bg: '#F5F5F5', icon: '📜' };
                  return (
                    <div key={b.id || idx} style={{ display: 'flex', gap: 12, padding: '12px 14px',
                      border: '1px solid var(--border)', borderRadius: 8, borderLeft: `4px solid ${info.color}`, background: '#FAFAFA' }}>
                      <div style={{ fontSize: 24, flexShrink: 0 }}>{info.icon}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                          <span className="badge" style={{ background: info.bg, color: info.color, fontSize: 10, fontWeight: 600 }}>
                            {b.famille_avis}
                          </span>
                          <span style={{ fontSize: 11, color: 'var(--text-light)' }}>
                            {b.date_parution ? new Date(b.date_parution).toLocaleDateString('fr-FR') : '-'}
                          </span>
                          {b.annonce_id && (
                            <span style={{ fontSize: 10, fontFamily: 'monospace', color: '#999' }}>{b.annonce_id}</span>
                          )}
                        </div>
                        {b.description && (() => {
                          const parsed = parseBodaccDescription(b.description);
                          if (parsed) {
                            return (
                              <div style={{ fontSize: 12, color: '#444', lineHeight: 1.6 }}>
                                {parsed.map((line, i) => (
                                  <div key={i} style={{ marginBottom: 2 }}>
                                    {i === 0 ? <strong>{line}</strong> : line}
                                  </div>
                                ))}
                              </div>
                            );
                          }
                          return <div style={{ fontSize: 12, color: '#444', lineHeight: 1.4 }}>{b.description}</div>;
                        })()}
                        {b.tribunal && (
                          <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 4 }}>{b.tribunal}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CollapseHeader>

        {/* Document Viewer Modal */}
        {docViewer && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={closeDocViewer}>
            <div style={{ background: 'white', borderRadius: 16, width: '92%', maxWidth: 1100, height: '90vh',
              display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
              onClick={e => e.stopPropagation()}>

              {/* Header */}
              <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)', display: 'flex',
                justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 16 }}>{docViewer.doc.document_type}</h3>
                  <div style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 4 }}>
                    {docViewer.doc.file_name} — {(docViewer.doc.file_size / 1024).toFixed(0)} Ko
                    <span className="badge" style={{ marginLeft: 8,
                      background: `${DOC_STATUS_MAP[docViewer.doc.status]?.color}20`,
                      color: DOC_STATUS_MAP[docViewer.doc.status]?.color, fontSize: 10 }}>
                      {DOC_STATUS_MAP[docViewer.doc.status]?.label || docViewer.doc.status}
                    </span>
                  </div>
                </div>
                <button onClick={closeDocViewer}
                  style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer',
                    color: 'var(--text-light)', padding: '4px 8px', lineHeight: 1 }}
                  title="Fermer">
                  &times;
                </button>
              </div>

              {/* Document preview area */}
              <div style={{ flex: 1, overflow: 'auto', background: '#F0F0F0' }}>
                {docViewer.mimeType?.startsWith('image/') ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 24 }}>
                    <img src={docViewer.blobUrl} alt={docViewer.doc.file_name}
                      style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8, boxShadow: '0 2px 12px rgba(0,0,0,0.15)' }} />
                  </div>
                ) : docViewer.mimeType === 'application/pdf' ? (
                  <iframe src={docViewer.blobUrl} title={docViewer.doc.file_name}
                    style={{ width: '100%', height: '100%', border: 'none' }} />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    height: '100%', gap: 16, color: 'var(--text-light)' }}>
                    <div style={{ fontSize: 48 }}>📄</div>
                    <div style={{ fontSize: 14 }}>
                      Aperçu non disponible pour ce type de fichier ({docViewer.mimeType || 'inconnu'})
                    </div>
                    <button onClick={() => handleDownloadDoc(docViewer.doc)} className="btn btn-primary" style={{ fontSize: 13 }}>
                      Télécharger pour consulter
                    </button>
                  </div>
                )}
              </div>

              {/* Action bar */}
              <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', background: '#FAFAFA', flexShrink: 0 }}>
                {/* Startup comment (read-only) */}
                {docViewer.doc.startup_comment && (
                  <div style={{ marginBottom: 10, padding: '8px 12px', background: '#E3F2FD', borderRadius: 8,
                    borderLeft: '3px solid #2196F3', fontSize: 12 }}>
                    <span style={{ fontWeight: 600, color: '#1565C0' }}>Commentaire de la startup :</span>{' '}
                    <span style={{ color: '#333' }}>{docViewer.doc.startup_comment}</span>
                  </div>
                )}
                {/* Admin comment + Reason */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <textarea value={docViewerAdminComment} onChange={e => setDocViewerAdminComment(e.target.value)}
                    placeholder="Commentaire administrateur (visible par la startup)..."
                    rows={1}
                    style={{ flex: 1, minWidth: 200, padding: '8px 12px', borderRadius: 8, border: '1px solid #90CAF9',
                      fontFamily: 'inherit', fontSize: 12, resize: 'vertical', background: '#FAFEFF' }} />
                  <textarea value={docViewerReason} onChange={e => setDocViewerReason(e.target.value)}
                    placeholder="Motif (obligatoire pour rejeter ou demander un complément)..."
                    rows={1}
                    style={{ flex: 1, minWidth: 200, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)',
                      fontFamily: 'inherit', fontSize: 12, resize: 'vertical' }} />
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => handleDownloadDoc(docViewer.doc)}
                      style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)',
                        background: 'white', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 500,
                        display: 'flex', alignItems: 'center', gap: 6 }}>
                      Télécharger
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => handleDocViewerAction('valide')}
                      style={{ padding: '8px 18px', borderRadius: 8, border: 'none',
                        background: '#27AE60', color: 'white', cursor: 'pointer', fontFamily: 'inherit',
                        fontSize: 12, fontWeight: 600 }}>
                      Valider
                    </button>
                    <button onClick={() => {
                        if (!docViewerReason.trim()) { toast.error('Veuillez saisir un motif pour le rejet.'); return; }
                        handleDocViewerAction('rejete', docViewerReason);
                      }}
                      style={{ padding: '8px 18px', borderRadius: 8, border: 'none',
                        background: '#E74C3C', color: 'white', cursor: 'pointer', fontFamily: 'inherit',
                        fontSize: 12, fontWeight: 600, opacity: docViewerReason.trim() ? 1 : 0.5 }}>
                      Rejeter
                    </button>
                    <button onClick={() => {
                        if (!docViewerReason.trim()) { toast.error('Veuillez saisir un motif pour la demande de complément.'); return; }
                        handleDocViewerAction('a_completer', docViewerReason);
                      }}
                      style={{ padding: '8px 18px', borderRadius: 8, border: 'none',
                        background: '#E67E22', color: 'white', cursor: 'pointer', fontFamily: 'inherit',
                        fontSize: 12, fontWeight: 600, opacity: docViewerReason.trim() ? 1 : 0.5 }}>
                      Demander un complément
                    </button>
                    <button onClick={closeDocViewer}
                      style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid var(--border)',
                        background: 'white', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 500 }}>
                      Fermer
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Audit Alerts */}
        {audit_alerts.length > 0 && (
          <div className="card">
            <h3 style={{ marginBottom: 16 }}>Alertes d'audit ({audit_alerts.length})</h3>
            {audit_alerts.map(a => (
              <div key={a.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <span className="badge" style={{ fontSize: 10, marginRight: 8,
                    background: a.severity === 'critique' ? '#FFEBEE' : '#FFF3E0',
                    color: a.severity === 'critique' ? '#C62828' : '#E65100' }}>
                    {a.severity}
                  </span>
                  {a.description || a.field_name}
                </div>
                <span className={`badge badge-${a.status === 'open' ? 'orange' : 'vert'}`} style={{ fontSize: 10 }}>
                  {a.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
